import React, { useState } from 'react';
import {
    AppBar,
    Toolbar,
    IconButton,
    Drawer,
    Box,
    Typography,
    ButtonBase,
    Badge
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { APP_LOGO_WHITE_URL, SETTINGS_PROFILE, SETTINGS_CHANGE_PASSWORD, SETTINGS_TERMS_AND_CONDITIONS } from '../../utils/constants';
import { useLocation, useNavigate } from 'react-router-dom';
import getTheme from '../../theme/theme';
import { PinnacleThemes } from '../../theme/types';

interface MobileSidebarProps {
    setIsSearchDialogBoxOpen: (open: boolean) => void;
    setIsLogoutDialogOpen: (open: boolean) => void;
    setIsSettingsDialogBoxOpen: (open: boolean) => void;
    setDialogBoxCurrentPath: (path: string) => void;
    setSearchTermForDialog: (term: string) => void;
    unreadNotification?: boolean;
}

const MobileSidebar: React.FC<MobileSidebarProps> = ({
    setIsSearchDialogBoxOpen,
    setIsLogoutDialogOpen,
    setIsSettingsDialogBoxOpen,
    setDialogBoxCurrentPath,
    setSearchTermForDialog,
    unreadNotification
}) => {
    const theme = getTheme(PinnacleThemes.BASE_THEME);
    const [open, setOpen] = useState(false);
    const [expandedItem, setExpandedItem] = useState<string | null>(null);
    const navigate = useNavigate();
    const location = useLocation();

    const drawerItems = [
        { text: 'Search', icon: <SearchIcon />, type: 'dialog' },
        {
            text: 'Notification', icon: (
                unreadNotification ? (
                    <Badge
                        color="error"
                        variant="dot"
                        invisible={false}
                        sx={{
                            '& .MuiBadge-dot': {
                                backgroundColor: theme.palette.secondary.main,
                                right: 3,
                                top: 1
                            },
                        }}
                    >
                        <NotificationsNoneOutlinedIcon />
                    </Badge>
                ) : (
                    <NotificationsNoneOutlinedIcon />
                )
            ), path: '/notifications', type: 'navigate'
        },
        { text: 'Home', icon: <HomeOutlinedIcon />, path: '/', type: 'navigate' },
        { text: 'Reference', icon: <LibraryBooksOutlinedIcon />, path: '/references', type: 'navigate' },
        {
            text: 'Settings',
            icon: <SettingsIcon />,
            type: 'subItems',
            subItems: [
                { label: 'Profile', path: SETTINGS_PROFILE },
                { label: 'Change Password', path: SETTINGS_CHANGE_PASSWORD },
                { label: 'Terms & Services', path: SETTINGS_TERMS_AND_CONDITIONS },
            ],
        },
        { text: 'Logout', icon: <LogoutOutlinedIcon />, type: 'action' },
    ];

    const toggleDrawer = (value: boolean) => () => setOpen(value);

    const handleItemClick = (item: any) => {
        if (item.type === 'subItems') {
            setExpandedItem(prev => prev === item.text ? null : item.text);
        } else if (item.type === 'action' && item.text === 'Logout') {
            setIsLogoutDialogOpen(true);
            setOpen(false);
        } else if (item.type === 'dialog' && item.text === 'Search') {
            const lastSearchTerm = sessionStorage.getItem('lastSearchTerm') || '';
            setSearchTermForDialog(lastSearchTerm);
            setIsSearchDialogBoxOpen(true);
            sessionStorage.setItem('searchDialogOpen', 'true');
            setDialogBoxCurrentPath(location.pathname);
            setOpen(false);
        } else if (item.type === 'navigate') {
            navigate(item.path);
            setOpen(false);
        }
    };

    const handleSubItemClick = (sub: any) => {
        if (sub.path === SETTINGS_TERMS_AND_CONDITIONS) {
            navigate('/terms');
        } else {
            setDialogBoxCurrentPath(sub.path);
            setIsSettingsDialogBoxOpen(true);
        }
        setExpandedItem(null);
        setOpen(false);
    };

    return (
        <>
            <AppBar
                position="fixed"
                sx={{ background: 'linear-gradient(73.79deg, #010911 48.31%, #0B4176 100.42%)' }}
            >
                <Toolbar>
                    <IconButton edge="start" color="inherit" onClick={toggleDrawer(true)}>
                        <MenuIcon />
                    </IconButton>
                    <Box
                        component="img"
                        src={APP_LOGO_WHITE_URL}
                        alt="CPA Logo"
                        sx={{
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            maxWidth: '150px',
                            height: 'auto',
                        }}
                        onClick={() => navigate('/')}
                    />
                </Toolbar>
            </AppBar>

            <Drawer anchor="left" open={open} onClose={toggleDrawer(false)}>
                <Box
                    sx={{
                        width: 180,
                        minHeight: '100vh',
                        background: 'linear-gradient(8.18deg, #010911 46.13%, #0B4176 141.32%)',
                        color: 'white',
                        display: 'flex',
                        flexDirection: 'column',
                        px: '16px',
                        pt: 17,
                    }}
                >
                    <IconButton
                        onClick={toggleDrawer(false)}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            color: 'white',
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        {drawerItems.map((item, index) => {
                            const isExpanded = expandedItem === item.text;
                            const isActive = item.path === location.pathname;

                            return (
                                <React.Fragment key={index}>
                                    <ButtonBase
                                        onClick={() => handleItemClick(item)}
                                        sx={{
                                            width: '100%',
                                            justifyContent: 'flex-start',
                                            backgroundColor: isActive ? '#FFD700' : 'transparent',
                                            color: isActive ? 'black' : 'white',
                                            borderRadius: '4px',
                                            py: 1,
                                            px: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            '&:hover': {
                                                backgroundColor: isActive
                                                    ? '#FFD700'
                                                    : 'rgba(255,255,255,0.1)',
                                            },
                                        }}
                                    >
                                        {item.icon}
                                        <Typography variant="body2" component="span" sx={{ color: isActive ? '#171717' : 'white', }}>
                                            {item.text}
                                        </Typography>
                                    </ButtonBase>

                                    {/* Sub Items */}
                                    {item.subItems && isExpanded && (
                                        <Box
                                            sx={{
                                                pt: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 3,
                                                pb: 4,
                                                pl: 1,
                                            }}
                                        >
                                            {item.subItems.map((sub: any, subIdx: number) => (
                                                <ButtonBase
                                                    key={subIdx}
                                                    onClick={() => handleSubItemClick(sub)}
                                                    sx={{
                                                        justifyContent: 'flex-start',
                                                        backgroundColor:
                                                            location.pathname === sub.path
                                                                ? '#FFD700'
                                                                : 'transparent',
                                                        color:
                                                            location.pathname === sub.path
                                                                ? 'black'
                                                                : 'white',
                                                        borderRadius: '4px',
                                                        px: 1,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        '&:hover': {
                                                            backgroundColor: 'transparent',
                                                        },
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: '0.85rem',
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {sub.label}
                                                    </Typography>
                                                </ButtonBase>
                                            ))}
                                        </Box>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </Box>
                </Box>
            </Drawer>

            <Toolbar disableGutters sx={{ px: 0, minHeight: { xs: 56, sm: 64 } }} />
        </>
    );
};

export default MobileSidebar;
